import { UserProfileForm } from '@grcs/types/UserRole';
import { createDatabaseService } from '../database/DatabaseService';
import { hashPassword, parseDOB } from '../auth/password';
import { normalizeStudentInput } from './studentInput';
import { getStudent } from './userManager';
export { generateStudentUsername } from './studentInput';

const checked = <T>(
  result: { data: T[] | null; error: any },
  step: string,
  requireRow = false,
): T[] => {
  if (
    result.error ||
    !result.data ||
    (requireRow && result.data.length !== 1)
  ) {
    throw new Error(
      `${step} failed. Some earlier steps may have completed; retry the same student details to finish enrollment.`,
    );
  }
  return result.data;
};

/** Both entry points use this workflow. Existing credentials are never reset on enrollment. */
export const addNewStudent = async (
  user: UserProfileForm,
  supabase: any,
  providedSemester?: string,
  classId?: string,
  className?: string,
) => {
  const input = normalizeStudentInput(user);
  const db = createDatabaseService(supabase);
  const semesters = checked(
    await db.semester.getCurrentSemesterYear(),
    'Active semester lookup',
    true,
  );
  const semester = semesters[0].semester_year;
  if (!semester || (providedSemester && providedSemester !== semester))
    throw new Error(
      'The active semester changed. Reload the page before adding students.',
    );
  if (!!classId !== !!className)
    throw new Error('A valid class and class name are required together.');
  if (classId) {
    const classes = checked(
      await db.class.getClassInfoByClassId(classId),
      'Class lookup',
      true,
    );
    if (
      classes[0].semester_year !== semester ||
      classes[0].class_name !== className
    )
      throw new Error(
        'The selected class is not active in this semester. Reload the class list.',
      );
  }
  const found = await getStudent(input, supabase);
  let profile = found.user;
  const created = !profile;
  if (!profile) {
    profile = checked(
      await db.user.addNewStudentUser({
        ...input,
        username: found.username,
        password: await hashPassword(parseDOB(input.dob)),
      }),
      'Student profile creation',
      true,
    )[0];
  }
  if (!profile.user_token)
    throw new Error(
      'Student profile has no identifier. Review the profile before enrolling.',
    );
  const token = profile.user_token;
  const current = checked(
    await db.student.getStudentBySemester(token, semester),
    'Enrollment lookup',
  );
  if (current.length > 1)
    throw new Error(
      'Duplicate enrollments exist for this semester. Review them before continuing.',
    );
  let previousClass = current[0]?.class_token;
  let previousSemester = semester;
  if (!current.length) {
    const previous = checked(
      await db.student.getStudentInfoByStudentId(token),
      'Previous enrollment lookup',
    );
    if (previous.length > 1)
      throw new Error(
        'Multiple previous enrollments exist. Review them before moving the student.',
      );
    if (previous.length) {
      previousClass = previous[0].class_token;
      previousSemester = previous[0].semester_year;
      checked(
        await db.student.reenrollStudentForCurrentSemester(
          token,
          semester,
          input.first_name,
          input.last_name,
          input.email,
          classId,
          className,
          previousSemester,
        ),
        'Semester re-enrollment',
        true,
      );
    } else {
      checked(
        await db.student.addNewStudentToStudentDB(
          token,
          input.first_name,
          input.last_name,
          input.email,
          semester,
        ),
        'Semester enrollment',
        true,
      );
    }
  }
  checked(
    await db.student.updateStudentSemesterInfo(token, semester, {
      first_name: input.first_name,
      last_name: input.last_name,
      email: input.email,
      ...(classId ? { class_token: classId, class_name: className } : {}),
    }),
    'Enrollment details',
    true,
  );
  const assignedClass = classId || (current.length ? previousClass : undefined);
  if (assignedClass) {
    checked(
      await db.class.updateClassStudentCount(assignedClass, semester),
      'Class count update',
      true,
    );
    checked(
      await db.homework.initializeHomeworkForStudent(
        token,
        assignedClass,
        `${input.first_name} ${input.last_name}`,
      ),
      'Homework assignment',
    );
  }
  if (
    previousClass &&
    (previousSemester !== semester || (classId && previousClass !== classId))
  ) {
    checked(
      await db.class.updateClassStudentCount(previousClass, previousSemester),
      'Previous class count update',
      true,
    );
  }
  return {
    data: [profile],
    error: null,
    outcome: created ? 'created' : current.length ? 'updated' : 'enrolled',
  };
};
